import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { projectsData } from '../data/projects';
import './ProjectGallery.css';

const ProjectGallery = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [imgErrors, setImgErrors] = useState({});

  const data = projectsData.find(p => p.id === category) || projectsData[0];

  useEffect(() => {
    window.scrollTo(0, 0);
    setImgErrors({});
    setLightbox(null);
    setTimeout(() => setLoaded(true), 100);
  }, [category]);

  
  const handleImgError = (idx) => {
    setImgErrors(prev => ({ ...prev, [idx]: true }));
  };

  return (
    <div className={`gallery-page ${loaded ? 'loaded' : ''}`}>

      {/* ── NAVBAR ── */}
      <nav className="gallery-nav">
        <button className="gallery-back" onClick={() => navigate('/')}>
          ← Back to Portfolio
        </button>

        <div className="gallery-nav-logo">PORT<span>FOLIO</span></div>

        <div className="gallery-nav-right">
          {projectsData.map(proj => (
            <button
              key={proj.id}
              className={`gallery-nav-cat ${category === proj.id ? 'active' : ''}`}
              onClick={() => navigate(`/projects/${proj.id}`)}
              style={category === proj.id
                ? { color: data.color, borderColor: data.color }
                : {}}
            >
              {proj.title}
            </button>
          ))}
        </div>
      </nav>

      {/* ── HERO ── */}
      <div className="gallery-hero" style={{ '--gc': data.color }}>
        <div className="gh-bg-text">{data.title.toUpperCase()}</div>
        <div className="gh-glow"
          style={{ background: `radial-gradient(circle, ${data.color}25 0%, transparent 65%)` }}>
        </div>

        <motion.div
          className="gh-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <div className="gh-breadcrumb">
            <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>Portfolio</span>
            <span className="gh-bc-sep">→</span>
            <span style={{ color: data.color }}>{data.category}</span>
          </div>

          <h1 className="gh-title">
            {data.title}
            <span style={{ color: data.color }}>.</span>
          </h1>

          <p className="gh-desc">{data.desc}</p>

          <div className="gh-tags-row">
            {data.tags.map((tag, i) => (
              <span
                key={i}
                className="gh-tag-pill"
                style={{ borderColor: `${data.color}50`, color: data.color }}
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="gh-meta">
            <div className="gh-meta-item">
              <span className="gh-meta-num" style={{ color: data.color }}>
                {data.images.length}
              </span>
              <span className="gh-meta-lbl">Images</span>
            </div>
            <div className="gh-meta-div"></div>
            <div className="gh-meta-item">
              <span className="gh-meta-num" style={{ color: data.color }}>
                {data.year}
              </span>
              <span className="gh-meta-lbl">Year</span>
            </div>
            <div className="gh-meta-div"></div>
            <div className="gh-meta-item">
              <span className="gh-meta-num" style={{ color: data.color }}>DK</span>
              <span className="gh-meta-lbl">Designer</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── GRID ── */}
      <div className="gallery-grid-section">
        {data.images.length > 0 ? (
          <>
            <div className="gallery-section-header">
              <span className="gsh-line"></span>
              <span className="gsh-text">
                {data.title} — {data.images.length} Work{data.images.length > 1 ? 's' : ''}
              </span>
              <span className="gsh-line"></span>
            </div>

            <div className="gallery-grid">
              {data.images.map((img, i) =>
                !imgErrors[i] ? (
                  <motion.div
                    key={i}
                    className="gallery-item"
                    style={{ '--gc': data.color }}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.07 }}
                    onClick={() => setLightbox(i)}
                  >
                    <div className="gi-img-wrap">
                      <img
                        src={img}
                        alt={`${data.title} ${i + 1}`}
                        className="gi-img"
                        loading="lazy"
                        onError={() => handleImgError(i)}
                      />
                      <div className="gi-overlay">
                        <div className="gi-overlay-inner">
                          <span className="gi-zoom">⊕</span>
                          <span className="gi-view-lbl">View Full</span>
                        </div>
                      </div>
                      <div className="gi-corner gi-tl"></div>
                      <div className="gi-corner gi-br"></div>
                    </div>
                    <div className="gi-footer">
                      <span className="gi-num" style={{ color: data.color }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="gi-name">
                        {data.title} · {i + 1}
                      </span>
                      <span className="gi-arr">→</span>
                    </div>
                  </motion.div>
                ) : (
                  <div
                    key={i}
                    className="gi-placeholder"
                    style={{ '--gc': data.color }}
                  >
                    <span className="gip-num" style={{ color: data.color }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="gip-label">Add Image</span>
                    <code className="gip-path">{img}</code>
                  </div>
                )
              )}
            </div>
          </>
        ) : (
          <motion.div
            className="gallery-empty"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="ge-circle" style={{ borderColor: data.color }}>
              <span style={{ color: data.color }}>◎</span>
            </div>
            <h3 className="ge-title">Coming Soon</h3>
            <p className="ge-desc">
              Projects for <strong style={{ color: data.color }}>{data.title}</strong> will be added soon.
            </p>
            <button
              className="ge-btn"
              style={{ background: data.color }}
              onClick={() => navigate('/')}
            >
              ← Back to Portfolio
            </button>
          </motion.div>
        )}
      </div>

      {/* ── LIGHTBOX ── */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            className="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox(null)}
          >
            {/* CLOSE */}
            <button
              className="glb-close"
              onClick={() => setLightbox(null)}
            >✕</button>

            {/* COUNTER */}
            <div className="glb-counter">
              <span style={{ color: data.color }}>
                {String(lightbox + 1).padStart(2, '0')}
              </span>
              <span className="glb-sep">/</span>
              <span>{String(data.images.length).padStart(2, '0')}</span>
            </div>

            {/* PREV */}
            <button
              className="glb-btn glb-prev"
              onClick={e => {
                e.stopPropagation();
                setLightbox(p => (p - 1 + data.images.length) % data.images.length);
              }}
            >‹</button>

            {/* IMAGE */}
            <motion.div
              className="glb-content"
              key={lightbox}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.3 }}
              onClick={e => e.stopPropagation()}
            >
              <img
                src={data.images[lightbox]}
                alt={`${data.title} ${lightbox + 1}`}
                className="glb-img"
              />
              <div className="glb-info">
                <span className="glb-title" style={{ color: data.color }}>
                  {data.title}
                </span>
                <span className="glb-subtitle">
                  Project {lightbox + 1} of {data.images.length}
                </span>
              </div>
            </motion.div>

            {/* NEXT */}
            <button
              className="glb-btn glb-next"
              onClick={e => {
                e.stopPropagation();
                setLightbox(p => (p + 1) % data.images.length);
              }}
            >›</button>

            {/* DOTS */}
            <div className="glb-dots">
              {data.images.map((_, i) => (
                <span
                  key={i}
                  className={`glb-dot ${i === lightbox ? 'active' : ''}`}
                  style={i === lightbox ? { background: data.color } : {}}
                  onClick={e => { e.stopPropagation(); setLightbox(i); }}
                ></span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default ProjectGallery;